import {
    ArrowsRightLeftIcon,
    BookOpenIcon,
    ChatBubbleLeftRightIcon,
    CloudArrowUpIcon,
    HomeIcon,
    MapIcon,
    SparklesIcon,
    UserGroupIcon,
} from '@heroicons/react/24/outline'

import { MOCK_CONFLICTS, MOCK_FAMILY, MOCK_PROVIDERS } from './mockData'

const OPEN_CONFLICTS = MOCK_CONFLICTS.filter((c) => c.status !== 'resolved')

export default function FamilyAppPreview() {
    return (
        <div className='flex-1 min-h-[calc(100vh-2.5rem)] bg-neutral-50 px-4 py-8'>
            <div className='max-w-3xl mx-auto'>
                <div className='mb-6 text-center'>
                    <h1 className='text-xl font-bold text-neutral-900'>Family-app preview</h1>
                    <p className='text-sm text-neutral-600 mt-1'>
                        How {MOCK_FAMILY.primaryParent.split(' ')[0]} sees the care-team synthesis on her phone.
                    </p>
                </div>

                <div className='flex justify-center'>
                    <div className='w-[340px] bg-white rounded-3xl shadow-xl border border-neutral-200 overflow-hidden'>
                        {/* Status bar */}
                        <div className='h-7 bg-neutral-900' />
                        {/* App content */}
                        <div className='bg-gradient-to-b from-brand-50 to-white'>
                            <div className='px-4 pt-4 pb-3 flex items-center justify-between'>
                                <div className='text-sm font-bold text-brand-700'>BrightPath</div>
                                <div className='w-8 h-8 rounded-full bg-brand-200' />
                            </div>

                            <div className='px-4'>
                                <div className='text-lg font-bold text-neutral-900'>
                                    Good morning, {MOCK_FAMILY.primaryParent.split(' ')[0]}
                                </div>
                                <div className='text-xs text-neutral-600 mb-3'>
                                    Here&apos;s your day at a glance.
                                </div>
                            </div>

                            {/* Conflict prompt card */}
                            {OPEN_CONFLICTS.length > 0 && (
                                <div className='mx-4 mb-3 p-3 rounded-xl bg-white border-2 border-red-200 shadow-sm'>
                                    <div className='flex items-center gap-1.5 mb-1.5'>
                                        <ArrowsRightLeftIcon className='w-3.5 h-3.5 text-red-600' />
                                        <span className='text-[10px] uppercase tracking-wider text-red-700 font-semibold'>
                                            Needs your input
                                        </span>
                                    </div>
                                    <div className='text-sm font-medium text-neutral-900 leading-tight mb-1'>
                                        {OPEN_CONFLICTS[0].title}
                                    </div>
                                    <p className='text-[11px] text-neutral-600'>
                                        Two of your providers gave different recommendations. Tap to answer a few
                                        quick questions and BrightPath will update Eli&apos;s roadmap.
                                    </p>
                                    <div className='mt-2 flex items-center gap-2'>
                                        <button className='flex-1 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md'>
                                            Resolve
                                        </button>
                                        <button className='py-1.5 px-2 text-xs font-medium text-neutral-600 bg-neutral-100 rounded-md'>
                                            Later
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Care team summary card */}
                            <div className='mx-4 mb-3 p-3 rounded-xl bg-gradient-to-br from-brand-50 to-white border border-brand-200'>
                                <div className='flex items-center gap-1.5 mb-2'>
                                    <SparklesIcon className='w-3.5 h-3.5 text-brand-700' />
                                    <span className='text-[10px] uppercase tracking-wider text-brand-700 font-semibold'>
                                        Your care team
                                    </span>
                                </div>
                                <div className='text-xs text-neutral-700 mb-2'>
                                    BrightPath is keeping {MOCK_FAMILY.childFirstName}&apos;s roadmap in sync with{' '}
                                    <span className='font-semibold'>{MOCK_PROVIDERS.length} providers</span>.
                                </div>
                                <div className='space-y-1.5'>
                                    {MOCK_PROVIDERS.slice(0, 3).map((p) => (
                                        <div
                                            key={p.id}
                                            className='flex items-center gap-2 p-2 bg-white rounded-lg border border-neutral-200'
                                        >
                                            <div className='w-6 h-6 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0'>
                                                <UserGroupIcon className='w-3 h-3 text-brand-700' />
                                            </div>
                                            <div className='flex-1 min-w-0'>
                                                <div className='text-[11px] font-medium text-neutral-900 leading-tight truncate'>
                                                    {p.clinicianName}
                                                </div>
                                                <div className='text-[9px] text-neutral-500'>
                                                    {p.organization}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className='mt-2 flex items-center justify-between text-[10px] text-brand-700 font-medium'>
                                    <span>View all {MOCK_PROVIDERS.length} providers</span>
                                    <span className='flex items-center gap-1'>
                                        <CloudArrowUpIcon className='w-3 h-3' />
                                        Upload
                                    </span>
                                </div>
                            </div>

                            {/* Today + roadmap cards */}
                            <div className='px-4 pb-4 space-y-2'>
                                <div className='p-3 bg-white rounded-xl border border-neutral-200'>
                                    <div className='text-[10px] uppercase tracking-wider text-brand-700 font-semibold'>
                                        Today
                                    </div>
                                    <div className='text-sm font-medium text-neutral-900 mt-0.5'>
                                        3 quick wins
                                    </div>
                                    <div className='text-xs text-neutral-600 mt-1'>
                                        Aligned with the ABA visual schedule and the OT sensory diet.
                                    </div>
                                </div>
                                <div className='p-3 bg-white rounded-xl border border-neutral-200'>
                                    <div className='text-[10px] uppercase tracking-wider text-brand-700 font-semibold'>
                                        Roadmap
                                    </div>
                                    <div className='text-sm font-medium text-neutral-900 mt-0.5'>
                                        Milestone 4 of 9
                                    </div>
                                    <div className='text-xs text-neutral-600 mt-1'>
                                        Last updated from Megan Foster&apos;s ABA progress note (May 10).
                                    </div>
                                </div>
                            </div>

                            <div className='border-t border-neutral-200 grid grid-cols-5 py-2 bg-white'>
                                <NavTab icon={HomeIcon} label='Home' active />
                                <NavTab icon={UserGroupIcon} label='Team' />
                                <NavTab icon={MapIcon} label='Roadmap' />
                                <NavTab icon={ChatBubbleLeftRightIcon} label='Chat' />
                                <NavTab icon={BookOpenIcon} label='Library' />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

function NavTab({
    icon: Icon,
    label,
    active,
}: {
    icon: React.ComponentType<{ className?: string }>
    label: string
    active?: boolean
}) {
    return (
        <div className={`flex flex-col items-center gap-0.5 ${active ? 'text-brand-600' : 'text-neutral-400'}`}>
            <Icon className='w-4 h-4' />
            <span className='text-[9px] font-medium'>{label}</span>
        </div>
    )
}
